import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import isFunction from 'lodash-es/isFunction';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { SidenavService } from '../sidenav.service';
import { SidenavItem } from './sidenav-item.interface';
import { FeatureFlagService } from 'src/app/main/shared/services/feature-flag.service';
import { selectUserSession } from 'src/app/main/shared/services/store/user/selector';

@Component({
  selector: 'fury-sidenav-item',
  templateUrl: './sidenav-item.component.html',
  styleUrls: ['./sidenav-item.component.scss'],
  animations: [
    trigger('dropdownOpen', [
      state(
        'false',
        style({
          height: 0,
        })
      ),
      state(
        'true',
        style({
          height: '*',
        })
      ),
      transition('false <=> true', animate('300ms cubic-bezier(.35, 0, .25, 1)')),
    ]),
  ],
  imports: [CommonModule, RouterModule, FlexLayoutModule, MatIconModule, TranslateModule],
})
export class SidenavItemComponent implements OnInit {
  @Input('item') item: SidenavItem;
  @Input('level') level: number;

  isCollapsed$ = this.sidenavService.collapsed$;
  dropdownOpen$: Observable<boolean>;
  isShown = false;

  constructor(
    private sidenavService: SidenavService,
    private router: Router,
    private store: Store,
    private featureFlagService: FeatureFlagService
  ) {
    this.dropdownOpen$ = this.sidenavService.currentlyOpen$.pipe(
      map(
        (currentlyOpen) =>
          this.item.subItems &&
          this.item.subItems.length > 0 &&
          currentlyOpen.indexOf(this.item) > -1
      )
    );
  }

  get levelClass() {
    return `level-${this.level}`;
  }

  ngOnInit() {
    this.getIsShown();
  }

  getIsShown() {
    if (!this.item.claims && !this.item.featureFlag) {
      this.isShown = true;
    } else if (this.item.claims) {
      this.store
        .select(selectUserSession)
        .pipe(
          filter((userSession) => !!userSession),
          map((userSession) => userSession.claims)
        )
        .subscribe((claims) => {
          this.isShown = this.item.claims.split(' ').some((claim) => claims.includes(claim));
        });
    } else if (this.item.featureFlag) {
      this.featureFlagService.features$.subscribe((features) => {
        this.isShown = features[this.item.featureFlag];
      });
    }
  }

  isRouter(r: string) {
    return !!r;
  }

  isFunction(routeOrFunction: string[] | Function) {
    return isFunction(routeOrFunction);
  }

  handleClick() {
    if (this.item.subItems && this.item.subItems.length > 0) {
      this.sidenavService.toggleItemOpen(this.item);
    } else if (
      typeof this.item.routeOrFunction === 'string' ||
      this.item.routeOrFunction instanceof String
    ) {
      this.router.navigate([this.item.routeOrFunction]);
    } else if (
      typeof this.item.routeOrFunction === 'function' ||
      this.item.routeOrFunction instanceof Function
    ) {
      this.item.routeOrFunction();
    } else {
      throw Error(
        'Could not determine what to do, Sidenav-Item has no routeOrFunction set AND does not contain any subItems'
      );
    }
  }

  getTextIcon(item: SidenavItem) {
    let result = '';

    if (item) {
      const name = item.name.split(' ');

      if (name.length > 0) {
        result += name[0].charAt(0).toUpperCase();
      }

      if (name.length > 1) {
        result += name[1].charAt(0).toLowerCase();
      }

      if (name.length === 1) {
        result += name[0].charAt(1).toLowerCase();
      }
    }

    return result;
  }
}
